import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { Log } from "ng2-logger";
import { ToolRequestsService } from "@services/tool-requests.service";
import { TableConfig } from "@app/shared/util/table-config.utils";
import { ToolRequest } from "@models/toolRequests.model";
import { Tool } from "@app/tool/model/tool.model";
import { TranslateService } from "@ngx-translate/core";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { ToolSet } from "@app/tool/model/toolSet.model";
import { ReplaceToolModalComponent } from "../replace-tool-modal/replace-tool-modal.component";
import { Location } from "@angular/common";
import { ConfirmationModalComponent } from "@components/confirmation-modal/confirmation-modal.component";
import { ToolRequestStatus } from "@app/tool/model/enums/tool-request-status.enum";
import { Subscription } from 'rxjs';
import { ObservableService } from '@app/core/services/observable.service';
import { ToolService } from '@app/core/services/tool.service';
import { ToolRequestLinesToolsTableComponent } from "@app/shared/pages/tool-request-lines-tools-table/tool-request-lines-tools-table";

@Component({
    selector: 'app-process-tool-request',
    templateUrl: './process-tool-request.component.html',
    styleUrls: ['./process-tool-request.component.scss']
})
export class ProcessToolRequestComponent implements OnInit, OnDestroy {
    @ViewChild(ReplaceToolModalComponent, {static: false}) replaceModal: ReplaceToolModalComponent;
    @ViewChild(ConfirmationModalComponent, {static: false}) rejectionConfirmModal: ConfirmationModalComponent;
    @ViewChild(ToolRequestLinesToolsTableComponent, {static: false}) table: ToolRequestLinesToolsTableComponent;

    basePath = 'tool-requests-lines';
    from = 'lines';
    _logger = Log.create(ProcessToolRequestComponent.name);

    tableConfig = new TableConfig();
    toolRequestId = 0;
    isLoading = false;
    toolsLoading = false;

    selectedToolRequestDetails: ToolRequest;
    selectedToolRequestTools: Array<Tool | ToolSet> = new Array<Tool | ToolSet>();
    replacementTools: Tool[];
    selectedTool: Tool;

    toolsReservedForLines: Tool [];
    numberOfToolRequestTools = 0;
    requestContainsUnavailableTools = false;
    consent = true;
    needConsent = false;

    showValidations: boolean;
    isConfirmationInProgress = false;
    observableServiceSubscription: Subscription;
    showFilters = true;
    allExpanded = false;

    constructor(private route: ActivatedRoute,
                private router: Router,
                private toastr: ToastsManager,
                private location: Location,
                private translateService: TranslateService,
                private toolRequestService: ToolRequestsService,
                private toolService: ToolService,
                observableService: ObservableService) {
        this.observableServiceSubscription = observableService.hasChanged$.subscribe(
            () => {
                this.getToolRequestDetails(this.toolRequestId);
            }
        )
    }

    ngOnInit() {
        this.route.params.subscribe(params => {
            this.toolRequestId = params['id'];
            this.getToolRequestDetails(this.toolRequestId);
        });
    }

    ngOnDestroy() {
        this.observableServiceSubscription.unsubscribe();
    }

    onBack() {
        this.location.back();
    }

    getToolRequestDetails(id: number) {
        this.isLoading = true;
        this._logger.info("getToolRequestDetails:: Retrieving details for tool request id", id);
        this.toolRequestService.getToolRequestDetails(id).subscribe(
            (data: ToolRequest) => {
                this._logger.info('getTolRequestDetails:: API Returned data', data);
                this.selectedToolRequestDetails = data;
                this.tableConfig.pager.count = data.requestTools.length || 0;
                this.getToolRequestTools(id);
                this.isLoading = false;
            },
            (error) => {
                this._logger.info('getTolRequestDetails:: API Returned error', error);
                this.isLoading = false;
            }
        );
    }

    getToolRequestTools(id: number, tableConfig = new TableConfig()) {
        this.toolsLoading = true;
        this._logger.info("getToolRequestTools:: Retrieving list of available replacement tools for toolId", id);
        this.numberOfToolRequestTools = 0;
        this.requestContainsUnavailableTools = false;
        this.toolsReservedForLines = [];
        tableConfig.pager.pageSize = 0;
        this.toolRequestService.getToolRequestTools(id, tableConfig).subscribe(
            (data) => {
                this._logger.info('getToolRequestTools:: API Returned data', data);
                this.numberOfToolRequestTools = data.total;
                const toolIds = [];
                data.items.forEach((tool: Tool | ToolSet) => {
                    if (tool instanceof Tool) {
                        toolIds.push(tool.id);
                    } else if (tool instanceof ToolSet) {
                        tool.tools.map(x => x.id).forEach(toolId => {
                            toolIds.push(toolId);
                        });
                    }
                });
                this.needConsent = false;
                this.consent = true;
                this.toolService.getToolsThatNeedLineSwitch(toolIds, this.selectedToolRequestDetails?.line?.id).subscribe(
                    (ids) => {
                        data.items.forEach((tool: Tool | ToolSet) => {
                            if (tool instanceof Tool) {
                                const toolId = ids.find(x => tool.id === x);
                                if (toolId && !this.selectedToolRequestDetails.isForAnIssue) {
                                    tool.shouldBeReplaced = false;
                                    tool.lineSwitch = true;
                                    this.needConsent = true;
                                    this.consent = false;
                                } else if (tool.shouldBeReplaced) {
                                    this.requestContainsUnavailableTools = true;
                                    return;
                                }

                                if (tool.isAlreadyReservedForLine) {
                                    this.toolsReservedForLines.push(tool);
                                }

                            } else if (tool instanceof ToolSet) {
                                const toolsThatShouldBeReplaced = tool.tools.filter(toolFromSet => toolFromSet.shouldBeReplaced);
                                if (toolsThatShouldBeReplaced.length > 0) {

                                    if (this.selectedToolRequestDetails.isForAnIssue) {
                                        this.requestContainsUnavailableTools = true;
                                        return;
                                    }

                                    let needsToSwitchLine = false;
                                    toolsThatShouldBeReplaced.forEach(t => {
                                        const toolId = ids.find(x => t.id === x);
                                        if (toolId) {
                                            t.shouldBeReplaced = false;
                                            t.lineSwitch = true;
                                            this.needConsent = true;
                                            this.consent = false;
                                            needsToSwitchLine = true;
                                        }
                                    });
                                    if (!needsToSwitchLine) {
                                        this.requestContainsUnavailableTools = true;
                                    }
                                    return;
                                }

                                const toolsReservedForLinesFiltered = tool.tools
                                    .filter(toolFromSet => toolFromSet.isAlreadyReservedForLine);
                                if (toolsReservedForLinesFiltered.length > 0) {
                                    this.toolsReservedForLines = this.toolsReservedForLines.concat(toolsReservedForLinesFiltered);
                                }
                            }
                        });

                        this.selectedToolRequestTools = data.items;
                        this.tableConfig.pager.count = data.total || 0;
                        this.toolsLoading = false;
                    });
            });
    }

    approveToolRequest() {
        this._logger.info("approve:: Approved tool request");
        this.isLoading = true;
        this.toolRequestService.approveToolRequestById(this.selectedToolRequestDetails.id).subscribe(
            (data) => {
                this._logger.info('approve:: Tool request approved', data);
                this.router.navigate([`tool-requests-lines`]);
                this.isLoading = false;
                this.toastr.success(this.buildApprovalMessage(this.selectedToolRequestDetails.name));
            },
            (error) => {
                this._logger.info('getToolRequestTools:: API Returned error', error);
                this.toastr.error(this.buildServerError(this.selectedToolRequestDetails.name));
                this.isLoading = false;
            }
        );
    }

    rejectToolRequest() {
        this._logger.info("reject:: Rejected tool request");
        this.rejectionConfirmModal.open();
    }

    onConfirmReject(reason: string) {
        this.isLoading = true;
        this.isConfirmationInProgress = true;
        this.toolRequestService.rejectToolRequestById(this.selectedToolRequestDetails.id, {closeReason: reason}).subscribe(
            (data) => {
                this.isConfirmationInProgress = false;
                this._logger.info('reject:: Tool request rejected', data);
                this.router.navigate([`tool-requests-lines`]);
                this.toastr.info(this.buildRejectionMessage(this.selectedToolRequestDetails.name));
                this.isLoading = false;
                this.rejectionConfirmModal.close();
            },
            (error) => {
                this.isConfirmationInProgress = false;
                this._logger.error('reject:: API Returned error', error);
                this.toastr.error(this.buildServerError(this.selectedToolRequestDetails.name));
                this.rejectionConfirmModal.close();
                this.isLoading = false;
            }
        );
    }

    buildApprovalMessage(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-lines.toasts.toolRequestApproved', {toolRequestName: toolRequestName});
    }

    buildRejectionMessage(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-lines.toasts.toolRequestRejected', {toolRequestName: toolRequestName});
    }

    buildServerError(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-lines.toasts.cannotProcess', {toolRequestName: toolRequestName})
    }

    onSortChange(tableConfig) {
        this._logger.info("onSortChange::", tableConfig);
        this.getToolRequestTools(this.selectedToolRequestDetails.id, this.tableConfig);
    }

    onFilter(tableConfig) {
        this._logger.info("onFilter::", tableConfig);
        this.getToolRequestTools(this.selectedToolRequestDetails.id, this.tableConfig);
    }

    onPageChange(tableConfig) {
        this._logger.info("onPageChange::", tableConfig);
        this.getToolRequestTools(this.selectedToolRequestDetails.id, this.tableConfig);
    }

    onToolReplace(tool: Tool) {
        this.selectedTool = tool;
        this.isLoading = true;
        this._logger.info("getReplacementTools:: Retrieving list of available replacement tools for toolId", this.toolRequestId);
        this.toolRequestService.getReplacementToolsForToolId(this.toolRequestId, this.selectedTool.id).subscribe(
            (data) => {
                this._logger.info('getReplacementTools:: API Returned data', data);
                this.replacementTools = data;
                this._logger.info("onToolReplace:: Opening replacement rejectionConfirmModal", tool);
                this.isLoading = false;
                this.replaceModal.open(tool);
            },
            (error) => {
                this._logger.info('getReplacementTools:: API Returned error', error);
                this.isLoading = false;
            }
        );
    }

    onConfirmReplace(replacementTool: Tool) {
        this._logger.info("onConfirmReplace:: Confirming replacement tool", replacementTool);
        this.confirmReplacementTool(replacementTool);
    }

    onRepair(selectedTool: Tool) {
        this._logger.info("onRepair:: Confirming repair for tool", selectedTool);

        this.isLoading = true;
        this.toolRequestService.repairTool(this.selectedToolRequestDetails, this.selectedTool.id)
            .subscribe(
                (data) => {
                    this._logger.info("confirmReplacementTool:: API Returned data", data);
                    this.isLoading = false;
                    this.toastr.info(this.translateService.instant('atom.tool-requests-lines.toasts.returnForRepairSuccess'
                        , {toolName: this.selectedTool.name}));

                    const toolRequest = new ToolRequest(data);
                    if (toolRequest.status === ToolRequestStatus.CLOSED) {
                        this.toastr.info(this.translateService.instant('atom.tool-requests-lines.toasts.closeToolRequest'
                            , {toolRequestName: toolRequest.name}));
                        this.router.navigate([`tool-requests-lines`]);
                    } else {
                        this.getToolRequestTools(this.selectedToolRequestDetails.id);
                    }

                }, (error) => {
                    this.toastr.error(this.translateService.instant('atom.tool-requests-lines.toasts.returnForRepairFailed'
                        , {toolName: this.selectedTool.name}));
                    this._logger.error("confirmReplacementTool:: API Returned error", error);
                    this.isLoading = false;
                }
            )
    }

    confirmReplacementTool(replacementTool: Tool) {
        this._logger.info("confirmReplacementTool:: toolRequest | selectedTool | replacementTool",
            this.selectedToolRequestDetails, this.selectedTool, replacementTool);

        this.isLoading = true;
        this.toolRequestService.replaceTool(this.selectedToolRequestDetails, this.selectedTool.id, replacementTool.id)
            .subscribe(
                (data) => {
                    this._logger.info("confirmReplacementTool:: API Returned data", data);
                    this.getToolRequestTools(this.selectedToolRequestDetails.id);
                    this.isLoading = false;

                }, (error) => {
                    this._logger.error("confirmReplacementTool:: API Returned error", error);
                    this.toastr.error(this.translateService.instant('atom.tool-requests-lines.toasts.cannotReplace',
                        {toolName: this.selectedTool.name, replaceName: replacementTool.name}))
                    this.isLoading = false;
                }
            )
    }

    canPerformApprove() {
        return !this.requestContainsUnavailableTools && this.selectedToolRequestDetails.isApprovable &&
            this.consent && !(this.hasToolsInTransport && this.hasToolsReservedForLine);
    }

    getPartNumberName() {
        if (this.selectedToolRequestDetails.partNumber) {
            return this.selectedToolRequestDetails.partNumber.name;
        }
        return this.translateService.instant('atom.tool-requests-lines.process.labels.noPartNumber');
    }

    openToolRequest(toolRequest: ToolRequest) {
        if (toolRequest.toolRequestToBeApproved.status === ToolRequestStatus.NEW) {
            this.toolRequestService.startApprovalById(toolRequest.toolRequestToBeApproved.id).subscribe(
                (data) => {
                    this._logger.info('startApproval:: Tool Request processing started', data);
                    this.router.navigate([`${this.basePath}/${toolRequest.toolRequestToBeApproved.id}`]);
                },
                (error) => {
                    this._logger.error('startApproval:: API Returned error', error);
                    this.toastr.error(this.buildCannotStartProcessErrorMessage(toolRequest.name));
                    this.router.navigate([`${this.basePath}/${toolRequest.toolRequestToBeApproved.id}`]);
                }
            );
        } else {
            this.router.navigate([`${this.basePath}/${toolRequest.toolRequestToBeApproved.id}`]);
        }
    }

    buildCannotStartProcessErrorMessage(toolRequestName) {
        return this.translateService.instant('atom.tool-requests-lines.toasts.cannotStartProcess', {toolRequestName: toolRequestName});
    }

    getToolRequestsToBeClosed() {
        let nameOfToolRequests = '';
        this.selectedToolRequestDetails.toolRequestsToBeClosed.forEach((toolRequest) => {
            nameOfToolRequests += toolRequest.name + ",";
            nameOfToolRequests = nameOfToolRequests.slice(0, -1);
        });
        return nameOfToolRequests;
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
    }

    toggleExpanded() {
        this.allExpanded = !this.allExpanded;
        this.table.toggleExpand(this.allExpanded);
    }

    private hasToolCondition(predicate: (tool: Tool) => boolean): boolean {
        if (!this.selectedToolRequestTools) {
            return false;
        }

        return this.selectedToolRequestTools.some((toolOrSet: Tool | ToolSet) => {
            if (toolOrSet instanceof Tool) {
                return predicate(toolOrSet);
            } else if (toolOrSet instanceof ToolSet) {
                return toolOrSet.tools.some(predicate);
            }
            return false;
        });
    }

    get hasToolsInTransport(): boolean {
        return this.hasToolCondition(t => t.isToolInTransport === true);
    }

    get hasToolsReservedForLine(): boolean {
        return this.hasToolCondition(t => t.isAlreadyReservedForLine === true);
    }
}
